'use client'

import { useWallet } from '@/providers/WalletContext'
import { useInviteStore } from '@/stores/invite-store'
import { useSigner } from '@account-kit/react'
import { useCallback, useEffect, useRef } from 'react'

// Key for storing referral code from /r/[code] visits
const REFERRAL_CODE_KEY = 'referral_code'
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 3000

export function clearPendingReferral() {
	localStorage.removeItem(REFERRAL_CODE_KEY)
}

export function setReferralCode(code: string) {
	localStorage.setItem(REFERRAL_CODE_KEY, code)
}

export function getReferralCode(): string | null {
	if (typeof window === 'undefined') return null
	return localStorage.getItem(REFERRAL_CODE_KEY)
}

export function useRegisterUser() {
	const { eoaAddress, authenticated } = useWallet()
	const signer = useSigner()
	const isProcessingRef = useRef(false)
	const lastProcessedAddressRef = useRef<string | null>(null)
	const retryCountRef = useRef(0)
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const showInviteModal = useInviteStore(s => s.showInviteModal)

	// Reset when user logs out
	useEffect(() => {
		if (!authenticated) {
			lastProcessedAddressRef.current = null
			isProcessingRef.current = false
			retryCountRef.current = 0
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current)
				retryTimerRef.current = null
			}
		}
	}, [authenticated])

	const attemptRegistration = useCallback(async () => {
		if (isProcessingRef.current) return
		if (!(authenticated && eoaAddress && signer?.inner)) return

		isProcessingRef.current = true

		try {
			const referralCode = localStorage.getItem(REFERRAL_CODE_KEY)
			const stampedRequest = await signer.inner.stampWhoami()

			const res = await fetch('/api/auth/register', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					stampedRequest,
					referralCode: referralCode || undefined,
				}),
			})

			const data = await res.json()

			// New user needs an invite code - show the modal
			if (data.needsInviteCode) {
				showInviteModal(stampedRequest)
				isProcessingRef.current = false
				return
			}

			// Handle errors — schedule retry if possible
			if (!res.ok) {
				console.error('Registration failed:', data.error)
				isProcessingRef.current = false
				scheduleRetry()
				return
			}

			// Success
			lastProcessedAddressRef.current = eoaAddress
			isProcessingRef.current = false
			retryCountRef.current = 0

			if (data.isNew) {
				clearPendingReferral()
			}
		} catch (err) {
			console.error('Failed to register user:', err)
			isProcessingRef.current = false
			scheduleRetry()
		}
	}, [authenticated, eoaAddress, signer, showInviteModal])

	const scheduleRetry = useCallback(() => {
		if (retryCountRef.current >= MAX_RETRIES) return

		retryCountRef.current += 1
		const delay = RETRY_DELAY_MS * retryCountRef.current // Increasing delay

		retryTimerRef.current = setTimeout(() => {
			retryTimerRef.current = null
			attemptRegistration()
		}, delay)
	}, [attemptRegistration])

	// biome-ignore lint/correctness/useExhaustiveDependencies: Need authenticated+signer to re-trigger after trading session retry
	useEffect(() => {
		// Skip if already processed this address
		if (lastProcessedAddressRef.current === eoaAddress) return

		attemptRegistration()
	}, [authenticated, eoaAddress, signer, attemptRegistration])

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current)
			}
		}
	}, [])
}
